import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  useDisclosure,
  Stack,
  Text,
  Link,
  useColorModeValue,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  VStack,
  Icon,
  Spinner,
  Badge,
  InputGroup,
  InputLeftElement,
  useToast,
  Button,
  Divider,
} from "@chakra-ui/react";
import {
  HamburgerIcon,
  CloseIcon,
  BellIcon,
  CheckIcon,
  MoonIcon,
  SunIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BASE_URL } from "../services/api";
import { motion } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";
import RoleImpersonationBadge from "./RoleImpersonationBadge";
import { getSidebarModules } from "../services/sidebarService";
import { getCurrentCompany } from "../services/companyService";
import {
  checkPushBackendAvailability,
  getNotificationHistory,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  getUnreadNotificationCount,
  registerPushInBackground,
  getPushSubscriptionStatus,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "../services/pushNotificationService";
import {
  getWhatsNewUnreadCount,
  getWhatsNewItems,
  markWhatsNewAsRead,
  markAllWhatsNewAsRead,
} from "../services/whatsNewService";
import {
  BsStickies,
  BsPeople,
  BsFileEarmarkText,
  BsPersonBadge,
  BsUiChecksGrid,
  BsGear,
  BsClipboardCheck,
  BsPlug,
  BsGraphUp,
  BsSignpost2,
  BsCalendarEvent,
  BsShieldCheck,
  BsCalendarCheck,
  BsClockHistory,
  BsCashStack,
  BsCreditCard,
  BsCreditCard2Back,
  BsBoxArrowRight,
  BsBank,
  BsReceipt,
  BsChatDots,
  BsMortarboard,
  BsPhone,
  BsBarChart,
  BsRocketTakeoff,
  BsCart,
  BsBoxSeam,
} from "react-icons/bs";

// Icon mapper - same as in Sidebar.js
const ICON_MAPPER = {
  BsPeople,
  BsFileEarmarkText,
  BsPersonBadge,
  BsUiChecksGrid,
  BsGear,
  BsClipboardCheck,
  BsPlug,
  BsGraphUp,
  BsSignpost2,
  BsCalendarEvent,
  BsShieldCheck,
  BsCalendarCheck,
  BsClockHistory,
  BsCashStack,
  BsCreditCard,
  BsCreditCard2Back,
  BsBoxArrowRight,
  BsBank,
  BsReceipt,
  BsChatDots,
  BsMortarboard,
  BsPhone,
  BsBarChart,
  BsRocketTakeoff,
  BsStickies,
  BsCart,
  BsBoxSeam,
};

const MotionLink = motion(Link);

const Links = [
  // { label: "Dashboard", href: "/Dashboard", disabled: false },
  // { label: "Core HR", href: "/core-hr/employee-database", disabled: false },
  // { label: "Recruitment", href: "/recruiting/ats", disabled: false },
  // { label: "Attendance", href: "/clockin", disabled: false },
  // { label: "Payroll", href: "/payroll/run", disabled: false },
  // { label: "Analytics", href: "/analytics/headcount", disabled: false },
  // { label: "Settings", href: "/settings", disabled: false },
];

const NavLink = ({ label, href, disabled, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === href;

  // Move all useColorModeValue calls to the top level
  const bgActive = useColorModeValue("teal.50", "teal.900");
  const bgHover = useColorModeValue("gray.100", "gray.700");
  const colorDisabled = useColorModeValue("gray.400", "gray.600");
  const colorActive = useColorModeValue("teal.700", "teal.200");
  const colorDefault = useColorModeValue("gray.700", "gray.200");
  const afterBg = useColorModeValue("teal.500", "teal.300");

  // Compute props using variables
  const bg = isActive ? bgActive : "transparent";
  let color;
  if (disabled) {
    color = colorDisabled;
  } else if (isActive) {
    color = colorActive;
  } else {
    color = colorDefault;
  }

  const _hover = disabled
    ? {}
    : {
        textDecoration: "none",
        bg: isActive ? bgActive : bgHover,
      };

  const _after = isActive
    ? {
        content: '""',
        position: "absolute",
        bottom: "-2px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "80%",
        height: "2px",
        bg: afterBg,
        borderRadius: "full",
      }
    : {};

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onNavigate) onNavigate();
    navigate(href);
  };

  return (
    <MotionLink
      whileHover={!disabled ? { y: -1 } : {}}
      transition={{ duration: 0.15 }}
      px={3}
      py={1.5}
      rounded="md"
      fontWeight={isActive ? "semibold" : "medium"}
      fontSize="sm"
      position="relative"
      bg={bg}
      color={color}
      _hover={_hover}
      _after={_after}
      pointerEvents={disabled ? "none" : "auto"}
      opacity={disabled ? 0.5 : 1}
      cursor={disabled ? "not-allowed" : "pointer"}
      onClick={handleClick}
    >
      {label}
    </MotionLink>
  );
};

const StickyNavbar = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSearchOpen,
    onOpen: onSearchOpen,
    onClose: onSearchClose,
  } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const { logout, user } = useAuth();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarModules, setSidebarModules] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchIndex, setSearchIndex] = useState(-1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [whatsNewUnreadCount, setWhatsNewUnreadCount] = useState(0);
  const [whatsNewItems, setWhatsNewItems] = useState([]);
  const [whatsNewLoading, setWhatsNewLoading] = useState(false);
  const [isPushBackendAvailable, setIsPushBackendAvailable] = useState(null);
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");
  const [pushLoading, setPushLoading] = useState(false);

  // Tenant company (for logo / name in brand block)
  const [tenantCompany, setTenantCompany] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("company") || "{}");
    } catch {
      return {};
    }
  });
  // Fetch fresh company data from API on mount so branding is always up to date
  useEffect(() => {
    let active = true;
    getCurrentCompany()
      .then((res) => {
        if (active && res?.success && res?.data?.company) {
          const company = res.data.company;
          setTenantCompany(company);
          // Persist so other components benefit too
          try {
            const stored = JSON.parse(localStorage.getItem("company") || "{}");
            localStorage.setItem(
              "company",
              JSON.stringify({
                ...stored,
                ...company,
                id: company.id || company._id || stored.id,
              }),
            );
          } catch {}
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const handleStorage = () => {
      try {
        setTenantCompany(JSON.parse(localStorage.getItem("company") || "{}"));
      } catch {}
    };
    // "storage" fires cross-tab; "company-updated" fires same-tab (dispatched by persistCompanyLocally)
    window.addEventListener("storage", handleStorage);
    window.addEventListener("company-updated", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("company-updated", handleStorage);
    };
  }, []);
  const tenantName = tenantCompany?.name || "";
  const tenantLogoFallbackUrl = useMemo(() => {
    const urlLogo = tenantCompany?.branding?.logoUrl;
    if (!urlLogo) return null;
    if (urlLogo.startsWith("http://") || urlLogo.startsWith("https://")) {
      return urlLogo;
    }

    const apiOrigin = BASE_URL.replace(/\/api\/v\d+\/?$/, "").replace(
      /\/$/,
      "",
    );
    return `${apiOrigin}${urlLogo.startsWith("/") ? "" : "/"}${urlLogo}`;
  }, [tenantCompany]);
  const tenantLogoUrl = useMemo(() => {
    const raw = tenantCompany?.branding?.logo;
    if (raw) {
      if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
      const apiOrigin = BASE_URL.replace(/\/api\/v\d+\/?$/, "").replace(
        /\/$/,
        "",
      );
      return `${apiOrigin}${raw.startsWith("/") ? "" : "/"}${raw}`;
    }
    return tenantLogoFallbackUrl;
  }, [tenantCompany, tenantLogoFallbackUrl]);

  // Check push subscription status on mount
  useEffect(() => {
    let mounted = true;
    async function checkStatus() {
      try {
        const status = await getPushSubscriptionStatus();
        if (mounted) {
          setIsPushSubscribed(!!status.subscribed);
          setPushPermission(status.permission);
        }
      } catch (e) {
        setIsPushSubscribed(false);
        setPushPermission("unsupported");
      }
    }
    checkStatus();
    return () => {
      mounted = false;
    };
  }, []);
  const [hasInitializedUnreadCount, setHasInitializedUnreadCount] =
    useState(false);

  const unreadHistoryCount = useMemo(
    () => notificationHistory.filter((item) => !item?.isRead).length,
    [notificationHistory],
  );
  const readHistoryCount = useMemo(
    () => notificationHistory.filter((item) => item?.isRead).length,
    [notificationHistory],
  );
  const totalNavbarUnreadCount = unreadCount + whatsNewUnreadCount;

  useEffect(() => {
    onClose(); // Auto-close mobile nav when route changes
  }, [location.pathname]);

  // Fetch sidebar modules for search
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setSearchLoading(true);
        const data = await getSidebarModules();
        // Map icons in modules
        const modulesWithIcons = (data || []).map((module) => ({
          ...module,
          items: module.items.map((item) => ({
            ...item,
            icon: ICON_MAPPER[item.icon] || BsStickies,
          })),
        }));
        setSidebarModules(modulesWithIcons);
      } catch (err) {
        console.error("Error fetching modules for search:", err);
        setSidebarModules([]);
      } finally {
        setSearchLoading(false);
      }
    };

    fetchModules();
  }, []);

  useEffect(() => {
    let intervalId;

    const refreshUnreadCount = async () => {
      try {
        const count = await getUnreadNotificationCount();
        if (
          hasInitializedUnreadCount &&
          count > unreadCount &&
          typeof window !== "undefined" &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          const latestNotifications = await getNotificationHistory(1, 0).catch(
            () => [],
          );
          const latest = latestNotifications?.[0];
          if (latest) {
            new Notification(latest.title || "New notification", {
              body: latest.message || "You have a new update.",
            });
          }
        }

        setUnreadCount(count);
        if (!hasInitializedUnreadCount) {
          setHasInitializedUnreadCount(true);
        }
      } catch (error) {
        // Keep bell usable even if unread count endpoint fails.
        console.warn("Failed to fetch unread notification count:", error);
      }
    };

    refreshUnreadCount();
    intervalId = setInterval(refreshUnreadCount, 15000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [hasInitializedUnreadCount, unreadCount]);

  useEffect(() => {
    let intervalId;

    const refreshWhatsNewUnread = async () => {
      try {
        const count = await getWhatsNewUnreadCount();
        setWhatsNewUnreadCount(count);
      } catch (error) {
        console.warn("Failed to fetch what's new unread count:", error);
      }
    };

    refreshWhatsNewUnread();
    intervalId = setInterval(refreshWhatsNewUnread, 60000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return undefined;
    }

    const handleServiceWorkerMessage = (event) => {
      if (event?.data?.type !== "PUSH_NOTIFICATION_RECEIVED") {
        return;
      }

      // Refresh immediately so bell count updates without opening menu.
      getUnreadNotificationCount()
        .then((count) => setUnreadCount(count))
        .catch(() => {});

      // Keep latest item in dropdown list if menu is opened shortly after.
      setNotificationHistory((prev) => {
        const incoming = event?.data?.payload;
        if (!incoming?.title && !incoming?.body) {
          return prev;
        }

        const optimisticItem = {
          _id: `sw-${Date.now()}`,
          title: incoming.title || "Notification",
          message: incoming.body || "New notification",
          createdAt: new Date().toISOString(),
          isRead: false,
          link: incoming?.data?.link || "/ticketing",
        };

        return [optimisticItem, ...prev].slice(0, 12);
      });
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage,
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    };
  }, []);

  useEffect(() => {
    const registerBackgroundPush = async () => {
      try {
        await registerPushInBackground({ requestPermission: false });
      } catch (error) {
        console.warn("Background push registration skipped:", error);
      }
    };

    registerBackgroundPush();
  }, []);

  useEffect(() => {
    let intervalId;

    const refreshPushBackendStatus = async () => {
      const isAvailable = await checkPushBackendAvailability();
      setIsPushBackendAvailable(isAvailable);
    };

    refreshPushBackendStatus();
    intervalId = setInterval(refreshPushBackendStatus, 120000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Memoized search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const results = [];
    const query = searchQuery.toLowerCase();

    sidebarModules.forEach((section) => {
      section.items.forEach((item) => {
        if (
          item.label.toLowerCase().includes(query) ||
          item.to.toLowerCase().includes(query)
        ) {
          results.push({
            ...item,
            section: section.heading,
          });
        }
      });
    });

    return results;
  }, [searchQuery, sidebarModules]);

  const handleSearchSelect = (item) => {
    navigate(item.to);
    setSearchQuery("");
    onSearchClose();
    setSearchIndex(-1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchIndex((prev) =>
        prev < searchResults.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter" && searchIndex >= 0) {
      e.preventDefault();
      handleSearchSelect(searchResults[searchIndex]);
    } else if (e.key === "Escape") {
      onSearchClose();
    }
  };

  const handleLogout = () => {
    // clear auth state and redirect to login
    try {
      logout();
    } catch (err) {
      console.warn("Logout failed:", err);
    }
    navigate("/login");
  };

  const handleMyProfile = () => {
    navigate("/MyProfile");
  };

  // Fetch and update notification history
  const refreshNotificationHistory = async () => {
    setNotificationLoading(true);
    try {
      const history = await getNotificationHistory();
      setNotificationHistory(history);
    } catch (error) {
      setNotificationHistory([]);
      console.warn("Failed to fetch notification history:", error);
    } finally {
      setNotificationLoading(false);
    }
  };

  // Toggle push notifications (subscribe/unsubscribe)
  const handleTogglePushNotifications = async () => {
    setPushLoading(true);
    try {
      const timeoutMs = 10000;
      let didTimeout = false;
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => {
          didTimeout = true;
          reject(new Error("Push notification request timed out."));
        }, timeoutMs),
      );
      try {
        if (!isPushSubscribed) {
          await Promise.race([
            subscribeToPushNotifications({ requestPermission: true }),
            timeoutPromise,
          ]);
          if (!didTimeout) {
            toast({
              title: "Push notifications enabled",
              description: "You will receive background ticket alerts.",
              status: "success",
              duration: 2500,
              isClosable: true,
            });
          }
        } else {
          await Promise.race([
            unsubscribeFromPushNotifications(),
            timeoutPromise,
          ]);
          if (!didTimeout) {
            toast({
              title: "Push notifications disabled",
              description: "You will no longer receive push alerts.",
              status: "info",
              duration: 2500,
              isClosable: true,
            });
          }
        }
      } catch (error) {
        toast({
          title: isPushSubscribed
            ? "Unable to disable notifications"
            : "Unable to enable notifications",
          description: error?.message || "Please try again in a moment.",
          status: "error",
          duration: 3500,
          isClosable: true,
        });
        console.error("Push toggle error:", error);
      }
      // Always update push state after attempt
      try {
        const status = await getPushSubscriptionStatus();
        setIsPushSubscribed(!!status.subscribed);
        setPushPermission(status.permission);
      } catch {
        setIsPushSubscribed(false);
        setPushPermission("unsupported");
      }
    } finally {
      setPushLoading(false);
    }
  };

  const refreshWhatsNewItems = async () => {
    setWhatsNewLoading(true);
    try {
      const items = await getWhatsNewItems({ limit: 5, onlyUnread: false });
      setWhatsNewItems(items);
    } catch (error) {
      setWhatsNewItems([]);
      console.warn("Failed to fetch what's new items:", error);
    } finally {
      setWhatsNewLoading(false);
    }
  };

  const handleMarkAllWhatsNewRead = async () => {
    try {
      await markAllWhatsNewAsRead();
      setWhatsNewUnreadCount(0);
      setWhatsNewItems((prev) =>
        prev.map((item) => ({
          ...item,
          isRead: true,
        })),
      );
    } catch (error) {
      toast({
        title: "Unable to mark product updates as read",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const handleWhatsNewItemClick = async (item) => {
    const itemId = item?.id || item?._id;

    if (!item?.isRead && itemId) {
      try {
        await markWhatsNewAsRead(itemId);
        setWhatsNewUnreadCount((prev) => Math.max(prev - 1, 0));
        setWhatsNewItems((prev) =>
          prev.map((row) =>
            (row?.id || row?._id) === itemId ? { ...row, isRead: true } : row,
          ),
        );
      } catch (error) {
        console.warn("Failed to mark what's new item as read:", error);
      }
    }

    navigate(item?.link || "/whats-new");
  };

  const handleBellMenuOpen = async () => {
    await Promise.all([
      refreshNotificationHistory(),
      refreshWhatsNewItems(),
      getUnreadNotificationCount()
        .then((count) => setUnreadCount(count))
        .catch(() => {}),
      getWhatsNewUnreadCount()
        .then((count) => setWhatsNewUnreadCount(count))
        .catch(() => {}),
    ]);
  };

  const handleNotificationItemClick = async (notification) => {
    const notificationId = notification?.id || notification?._id;

    if (!notificationId) {
      navigate(notification?.link || "/ticketing");
      return;
    }

    if (!notification?.isRead) {
      try {
        await markNotificationAsRead(notificationId);
      } catch (error) {
        console.warn("Failed to mark notification as read:", error);
      }
    }

    const nextCount = await getUnreadNotificationCount().catch(
      () => unreadCount,
    );
    setUnreadCount(nextCount);
    navigate(notification?.link || "/ticketing");
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setUnreadCount(0);
      setNotificationHistory((prev) =>
        prev.map((item) => ({ ...item, isRead: true })),
      );
      toast({
        title: "All notifications marked as read",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Unable to mark all as read",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatNotificationTime = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <Box
      position="sticky"
      top="0"
      zIndex="999"
      bg={useColorModeValue("white", "gray.800")}
      px={{ base: 3, md: 6 }}
      backdropFilter="saturate(180%) blur(12px)"
      boxShadow="sm"
      borderBottom="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
    >
      <Flex
        h={14}
        alignItems="center"
        justifyContent="space-between"
        gap={{ base: 2, md: 8 }}
      >
        {/* Left Core: Logo + Nav Links */}
        <Flex align="center" gap={8} flex="1">
          {/* Brand Block - Clickable to go to Dashboard */}
          <Flex
            align="center"
            gap={3}
            flexShrink={0}
            onClick={handleLogoClick}
            cursor="pointer"
            transition="all 0.2s ease"
            _hover={{ opacity: 0.85 }}
            title="Go to Dashboard"
          >
            {tenantLogoUrl ? (
              <>
                {/* NoraHuman mini — hidden on mobile */}
                <Flex
                  align="center"
                  gap={1.5}
                  display={{ base: "none", sm: "flex" }}
                >
                  <Image
                    src="/image.png"
                    alt="NoraHuman"
                    boxSize="24px"
                    opacity={0.8}
                  />
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={useColorModeValue("gray.500", "gray.400")}
                    letterSpacing="wide"
                  >
                    NoraHuman
                  </Text>
                </Flex>
                <Box
                  w="1px"
                  h="30px"
                  bg={useColorModeValue("gray.200", "gray.600")}
                  display={{ base: "none", sm: "block" }}
                />
                <Image
                  src={tenantLogoUrl}
                  fallbackSrc={tenantLogoFallbackUrl || undefined}
                  alt={tenantName || "Company"}
                  h="32px"
                  maxW={{ base: "90px", md: "150px" }}
                  objectFit="contain"
                  borderRadius="sm"
                />
              </>
            ) : (
              <>
                <Image
                  src="/image.png"
                  alt="NoraHuman"
                  boxSize={{ base: "28px", md: "36px" }}
                />
                <Box display={{ base: "none", sm: "block" }}>
                  <Text
                    fontWeight="bold"
                    fontSize="md"
                    bgGradient="linear(to-r, gray.900, green.500)"
                    bgClip="text"
                    lineHeight="1.2"
                  >
                    NoraHuman
                  </Text>
                  {tenantName && (
                    <Text
                      fontSize="9px"
                      color={useColorModeValue("gray.500", "gray.400")}
                      fontWeight="medium"
                      letterSpacing="widest"
                      lineHeight="1"
                      textTransform="uppercase"
                      noOfLines={1}
                      maxW="120px"
                    >
                      {tenantName}
                    </Text>
                  )}
                </Box>
              </>
            )}
          </Flex>

          {/* Desktop Nav Links */}
          <HStack spacing={1} display={{ base: "none", md: "flex" }}>
            {Links.map((link) => (
              <NavLink key={link.label} {...link} />
            ))}
          </HStack>
        </Flex>

        {/* Right Utility Cluster */}
        <HStack spacing={2} flexShrink={0}>
          {/* Search */}
          <IconButton
            icon={<SearchIcon />}
            onClick={onSearchOpen}
            variant="ghost"
            size="sm"
            aria-label="Search menu"
            title="Search menu (Ctrl+K)"
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700"),
            }}
          />

          {/* Dark/Light Toggle — hidden on mobile */}
          <IconButton
            icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
            onClick={toggleColorMode}
            variant="ghost"
            size="sm"
            aria-label="Toggle theme"
            display={{ base: "none", sm: "flex" }}
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700"),
            }}
          />

          {/* Notification Bell */}
          <Menu onOpen={handleBellMenuOpen} closeOnSelect={false}>
            <MenuButton
              as={IconButton}
              icon={
                <Box
                  position="relative"
                  lineHeight={0}
                  p={0.5}
                  borderRadius="full"
                  bg={
                    unreadCount > 0
                      ? useColorModeValue("red.50", "red.900")
                      : "transparent"
                  }
                >
                  <BellIcon />
                  {isPushBackendAvailable !== null && (
                    <Box
                      position="absolute"
                      top="-2px"
                      left="-2px"
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={isPushBackendAvailable ? "green.400" : "orange.400"}
                      border="1px solid"
                      borderColor={useColorModeValue("white", "gray.800")}
                    />
                  )}
                  {totalNavbarUnreadCount > 0 && (
                    <Badge
                      position="absolute"
                      top="-6px"
                      right="-8px"
                      borderRadius="full"
                      colorScheme="red"
                      fontSize="0.55rem"
                      minW="18px"
                      h="18px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      px={1}
                    >
                      {totalNavbarUnreadCount > 99
                        ? "99+"
                        : totalNavbarUnreadCount}
                    </Badge>
                  )}
                  {totalNavbarUnreadCount === 0 && (
                    <Box
                      position="absolute"
                      top="-6px"
                      right="-8px"
                      borderRadius="full"
                      bg="green.500"
                      color="white"
                      minW="18px"
                      h="18px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      border="1px solid"
                      borderColor={useColorModeValue("white", "gray.800")}
                    >
                      <CheckIcon boxSize={2.5} />
                    </Box>
                  )}
                </Box>
              }
              variant="ghost"
              size="sm"
              aria-label="Notifications"
              title={
                isPushBackendAvailable === null
                  ? "Notifications"
                  : isPushBackendAvailable
                    ? "Notifications (push connected)"
                    : "Notifications (push backend unavailable)"
              }
              _hover={{
                bg: useColorModeValue("gray.100", "gray.700"),
              }}
            />
            <MenuList
              minW={{ base: "calc(100vw - 24px)", sm: "340px" }}
              maxW={{ base: "calc(100vw - 24px)", sm: "380px" }}
              p={0}
              bg={useColorModeValue("white", "gray.800")}
              borderColor={useColorModeValue("gray.200", "gray.700")}
              borderRadius="xl"
              overflow="hidden"
              boxShadow="xl"
            >
              <Box
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={useColorModeValue("gray.200", "gray.700")}
                bg={useColorModeValue("gray.50", "whiteAlpha.100")}
              >
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="semibold">
                      Notifications
                    </Text>
                    <HStack spacing={1.5}>
                      <Badge colorScheme="red" borderRadius="full">
                        {totalNavbarUnreadCount} remaining
                      </Badge>
                      <Badge colorScheme="green" borderRadius="full">
                        {readHistoryCount} read
                      </Badge>
                    </HStack>
                  </HStack>
                  <HStack justify="space-between">
                    <Badge
                      colorScheme={isPushBackendAvailable ? "green" : "orange"}
                      variant="subtle"
                    >
                      {isPushBackendAvailable ? "Push online" : "Push offline"}
                    </Badge>
                    <Text
                      fontSize="xs"
                      color={useColorModeValue("gray.600", "gray.400")}
                    >
                      {unreadHistoryCount > 0
                        ? `${unreadHistoryCount} unread in current list`
                        : "Everything in this list is read"}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Box
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={useColorModeValue("gray.200", "gray.700")}
              >
                <HStack justify="space-between" mb={2}>
                  <HStack spacing={2}>
                    <Icon
                      as={BsRocketTakeoff}
                      color={useColorModeValue("teal.600", "teal.300")}
                      boxSize={4}
                    />
                    <Text fontSize="sm" fontWeight="semibold">
                      What&apos;s New
                    </Text>
                    {whatsNewUnreadCount > 0 && (
                      <Badge colorScheme="teal" borderRadius="full">
                        {whatsNewUnreadCount} new
                      </Badge>
                    )}
                  </HStack>

                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={handleMarkAllWhatsNewRead}
                    isDisabled={whatsNewUnreadCount === 0}
                  >
                    Mark read
                  </Button>
                </HStack>

                {whatsNewLoading ? (
                  <Flex align="center" justify="center" py={4}>
                    <Spinner size="sm" color="teal.500" />
                  </Flex>
                ) : whatsNewItems.length === 0 ? (
                  <Text
                    fontSize="xs"
                    color={useColorModeValue("gray.500", "gray.400")}
                  >
                    No product updates yet.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={1}>
                    {whatsNewItems.slice(0, 3).map((item) => (
                      <Box
                        key={item?.id || item?._id}
                        px={3}
                        py={2}
                        borderRadius="md"
                        cursor="pointer"
                        bg={
                          item?.isRead
                            ? "transparent"
                            : useColorModeValue("teal.50", "teal.900")
                        }
                        _hover={{
                          bg: useColorModeValue("gray.50", "gray.700"),
                        }}
                        onClick={() => handleWhatsNewItemClick(item)}
                      >
                        <HStack align="flex-start" spacing={2}>
                          {!item?.isRead && (
                            <Box
                              mt="6px"
                              w="7px"
                              h="7px"
                              borderRadius="full"
                              bg="teal.400"
                              flexShrink={0}
                            />
                          )}

                          <Box flex="1" minW={0}>
                            <HStack spacing={2}>
                              <Text
                                fontSize="xs"
                                fontWeight={
                                  item?.isRead ? "medium" : "semibold"
                                }
                                noOfLines={1}
                              >
                                {item?.title || "Product update"}
                              </Text>

                              {item?.category && (
                                <Badge
                                  size="sm"
                                  colorScheme={
                                    item.category === "FEATURE"
                                      ? "green"
                                      : item.category === "FIX"
                                        ? "orange"
                                        : "blue"
                                  }
                                  fontSize="9px"
                                >
                                  {item.category}
                                </Badge>
                              )}
                            </HStack>

                            <Text
                              fontSize="11px"
                              color={useColorModeValue("gray.600", "gray.400")}
                              noOfLines={2}
                            >
                              {item?.summary ||
                                item?.description ||
                                "Product update"}
                            </Text>
                          </Box>
                        </HStack>
                      </Box>
                    ))}

                    <Button
                      size="xs"
                      variant="ghost"
                      justifyContent="center"
                      onClick={() => navigate("/whats-new")}
                    >
                      View all product updates
                    </Button>
                  </VStack>
                )}
              </Box>

              <HStack
                px={3}
                py={2}
                spacing={2}
                borderBottom="1px solid"
                borderColor={useColorModeValue("gray.100", "gray.700")}
                bg={useColorModeValue("gray.50", "gray.750")}
              >
                <Button
                  onClick={handleTogglePushNotifications}
                  fontSize="xs"
                  size="xs"
                  variant={isPushSubscribed ? "solid" : "outline"}
                  colorScheme={isPushSubscribed ? "red" : "teal"}
                  borderRadius="md"
                  isDisabled={
                    pushPermission === "denied" ||
                    !isPushBackendAvailable ||
                    pushLoading
                  }
                  isLoading={pushLoading}
                  loadingText={isPushSubscribed ? "Disabling" : "Enabling"}
                >
                  {isPushSubscribed ? "Disable push" : "Enable push"}
                </Button>
                <Button
                  onClick={handleMarkAllAsRead}
                  fontSize="xs"
                  size="xs"
                  variant="outline"
                  colorScheme="blue"
                  borderRadius="md"
                  isDisabled={unreadCount === 0}
                >
                  Mark all read
                </Button>
              </HStack>

              <Box
                px={4}
                py={2}
                borderBottom="1px solid"
                borderColor={useColorModeValue("gray.100", "gray.700")}
                bg={useColorModeValue("white", "gray.800")}
              >
                <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                  Operational Notifications
                </Text>
              </Box>

              <Box maxH="320px" overflowY="auto">
                {notificationLoading ? (
                  <Flex align="center" justify="center" py={8}>
                    <Spinner size="sm" color="teal.500" />
                  </Flex>
                ) : notificationHistory.length === 0 ? (
                  <Box px={4} py={6}>
                    <Text
                      fontSize="sm"
                      color={useColorModeValue("gray.500", "gray.400")}
                    >
                      No notification history yet.
                    </Text>
                  </Box>
                ) : (
                  notificationHistory.map((notification, index) => {
                    const notificationId =
                      notification?.id || notification?._id;
                    return (
                      <Box key={String(notificationId)}>
                        <MenuItem
                          onClick={() =>
                            handleNotificationItemClick(notification)
                          }
                          alignItems="flex-start"
                          py={3}
                          px={3}
                          whiteSpace="normal"
                          bg={
                            notification?.isRead
                              ? "transparent"
                              : useColorModeValue("blue.50", "blue.900")
                          }
                          _hover={{
                            bg: notification?.isRead
                              ? useColorModeValue("gray.50", "gray.700")
                              : useColorModeValue("blue.100", "blue.800"),
                          }}
                        >
                          <Box
                            w="100%"
                            pl={2}
                            borderLeft="3px solid"
                            borderLeftColor={
                              notification?.isRead
                                ? useColorModeValue("gray.200", "gray.600")
                                : useColorModeValue("blue.400", "blue.300")
                            }
                          >
                            <HStack spacing={2} align="center" mb={1}>
                              <Text
                                fontSize="sm"
                                fontWeight={
                                  notification?.isRead ? "medium" : "semibold"
                                }
                                color={useColorModeValue(
                                  "gray.800",
                                  "gray.100",
                                )}
                                noOfLines={1}
                              >
                                {notification?.title || "Notification"}
                              </Text>
                              {!notification?.isRead && (
                                <Box
                                  w="7px"
                                  h="7px"
                                  borderRadius="full"
                                  bg="blue.400"
                                />
                              )}
                            </HStack>
                            <Text
                              fontSize="xs"
                              color={useColorModeValue("gray.600", "gray.400")}
                              noOfLines={2}
                            >
                              {notification?.message || "No details available."}
                            </Text>
                            <Text
                              mt={1}
                              fontSize="10px"
                              color={useColorModeValue("gray.500", "gray.500")}
                            >
                              {formatNotificationTime(notification?.createdAt)}
                            </Text>
                          </Box>
                        </MenuItem>
                        {index !== notificationHistory.length - 1 && (
                          <Divider
                            borderColor={useColorModeValue(
                              "gray.100",
                              "gray.700",
                            )}
                          />
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>

              <MenuItem
                onClick={() => navigate("/tech/notifications")}
                fontSize="sm"
                justifyContent="center"
                borderTop="1px solid"
                borderColor={useColorModeValue("gray.200", "gray.700")}
              >
                View all notifications
              </MenuItem>
            </MenuList>
          </Menu>

          {/* Role Impersonation Badge — hidden on mobile */}
          <Box display={{ base: "none", md: "flex" }}>
            <RoleImpersonationBadge />
          </Box>

          {/* Profile Menu */}
          <Menu>
            <MenuButton
              as={IconButton}
              variant="ghost"
              size="sm"
              icon={
                <Avatar
                  size="sm"
                  name={user?.name || "User"}
                  src="/profile.png"
                />
              }
              _hover={{
                bg: useColorModeValue("gray.100", "gray.700"),
              }}
            />
            <MenuList>
              <MenuItem onClick={() => navigate("/Dashboard")}>
                Dashboard
              </MenuItem>
              <MenuItem onClick={handleMyProfile}>My Profile</MenuItem>
              <MenuItem onClick={() => navigate("/settings")}>
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </MenuList>
          </Menu>

          {/* Hamburger */}
          <IconButton
            size="sm"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label="Toggle Navigation"
            display={{ md: "none" }}
            onClick={isOpen ? onClose : onOpen}
            variant="ghost"
            _hover={{
              bg: useColorModeValue("gray.100", "gray.700"),
            }}
          />
        </HStack>
      </Flex>

      {/* Mobile Nav */}
      {isOpen && (
        <Box pb={3} display={{ md: "none" }}>
          <Stack as="nav" spacing={1}>
            {Links.map((link) => (
              <NavLink key={link.label} {...link} onNavigate={onClose} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Search Modal - Command Palette Style */}
      <Modal
        isOpen={isSearchOpen}
        onClose={onSearchClose}
        size="lg"
        motionPreset="fadeIn"
      >
        <ModalOverlay backdropFilter="blur(5px)" bg="rgba(0,0,0,0.4)" />
        <ModalContent
          borderRadius="lg"
          mt="60px"
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          bg={useColorModeValue("white", "gray.800")}
          border="1px solid"
          borderColor={useColorModeValue("gray.200", "gray.700")}
          maxW="600px"
          overflow="hidden"
        >
          {/* Clean Search Header */}
          <Box
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor={useColorModeValue("gray.200", "gray.700")}
            bg={useColorModeValue("white", "gray.800")}
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon
                  color={useColorModeValue("gray.400", "gray.500")}
                  fontSize="18px"
                />
              </InputLeftElement>
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchIndex(-1);
                }}
                onKeyDown={handleSearchKeyDown}
                autoFocus
                size="md"
                border="none"
                _focus={{
                  outline: "none",
                  boxShadow: "none",
                }}
                fontSize="15px"
                pl={10}
              />
            </InputGroup>
          </Box>

          {/* Results */}
          <ModalBody p={0} maxH="400px" overflowY="auto">
            {searchLoading ? (
              <Flex justify="center" align="center" py={12}>
                <Spinner color="teal.500" size="md" />
              </Flex>
            ) : !searchQuery ? (
              <Box px={4} py={8} textAlign="center">
                <Text
                  fontSize="sm"
                  color={useColorModeValue("gray.500", "gray.400")}
                >
                  Start typing to search...
                </Text>
              </Box>
            ) : searchResults.length === 0 ? (
              <Box px={4} py={8} textAlign="center">
                <Text
                  fontSize="sm"
                  color={useColorModeValue("gray.500", "gray.400")}
                >
                  No results found
                </Text>
              </Box>
            ) : (
              <VStack spacing={0} align="stretch">
                {searchResults.map((item, idx) => (
                  <Box
                    key={item.to}
                    px={4}
                    py={3}
                    cursor="pointer"
                    bg={
                      idx === searchIndex
                        ? useColorModeValue("teal.50", "teal.900")
                        : "transparent"
                    }
                    borderBottom="1px solid"
                    borderColor={useColorModeValue("gray.100", "gray.700")}
                    _last={{ borderBottom: "none" }}
                    _hover={{
                      bg: useColorModeValue("gray.50", "gray.700"),
                    }}
                    transition="background 0.15s ease"
                    onClick={() => handleSearchSelect(item)}
                    onMouseEnter={() => setSearchIndex(idx)}
                  >
                    <HStack spacing={3} align="center">
                      <Flex
                        w={8}
                        h={8}
                        borderRadius="md"
                        bg={useColorModeValue("teal.100", "teal.900")}
                        align="center"
                        justify="center"
                        flexShrink={0}
                      >
                        <Icon
                          as={
                            item.icon instanceof Function
                              ? item.icon
                              : ICON_MAPPER[item.icon] || BsStickies
                          }
                          boxSize={4}
                          color={useColorModeValue("teal.600", "teal.300")}
                        />
                      </Flex>
                      <Box flex={1} minW={0}>
                        <HStack spacing={2} align="center">
                          <Text
                            fontWeight="500"
                            fontSize="14px"
                            color={useColorModeValue("gray.900", "gray.100")}
                          >
                            {item.label}
                          </Text>
                          <Text
                            fontSize="11px"
                            color={useColorModeValue("gray.500", "gray.400")}
                          >
                            {item.section}
                          </Text>
                        </HStack>
                        <Text
                          fontSize="12px"
                          color={useColorModeValue("gray.500", "gray.400")}
                          noOfLines={1}
                        >
                          {item.to}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            )}
          </ModalBody>

          {/* Footer - keyboard hints */}
          {searchQuery && searchResults.length > 0 && (
            <Box
              px={4}
              py={2}
              borderTop="1px solid"
              borderColor={useColorModeValue("gray.200", "gray.700")}
              bg={useColorModeValue("gray.50", "gray.750")}
            >
              <HStack justify="space-between" fontSize="11px" color="gray.500">
                <Text>↑↓ navigate</Text>
                <Text>⏎ select</Text>
                <Text>esc close</Text>
              </HStack>
            </Box>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default StickyNavbar;
